/**
 * Custom hook for managing Phaser game initialization and lifecycle.
 * Extracts Phaser game logic from App.tsx.
 */

import { useEffect, useRef, useState } from 'react';
import { useGameStateStore } from '@shared/stores';

// Type for Phaser Game (avoiding top-level import for lazy loading)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PhaserGame = any;

interface UsePhaserGameOptions {
  onGameReady?: (game: PhaserGame) => void;
  onSceneReady?: (scene: unknown) => void;
}

/**
 * Hook for managing Phaser game initialization.
 */
export function usePhaserGame(
  gameContainerRef: React.RefObject<HTMLDivElement>,
  options?: UsePhaserGameOptions
) {
  const phaserGameRef = useRef<PhaserGame | null>(null);
  const optionsRef = useRef<UsePhaserGameOptions | undefined>(options);
  const { currentScreen, returnToMenu, setScreen } = useGameStateStore();
  const [isGameReady, setIsGameReady] = useState(false);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // Update options ref when options change (without causing effect re-run)
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Lazy load game assets when loading screen is shown
  useEffect(() => {
    if (currentScreen === 'loading' && !isLoadingAssets) {
      setIsLoadingAssets(true);

      // Dynamically import Phaser and game modules
      Promise.all([
        import('phaser'),
        import('@game/map/TilemapScene'),
        import('@game/map/createStaticMap'),
      ])
        .then(() => {
          // Assets loaded successfully, transition to game screen
          setScreen('game');
          setIsLoadingAssets(false);
        })
        .catch((error) => {
          console.error('Failed to load game assets:', error);
          setIsLoadingAssets(false);
          returnToMenu();
        });
    }
  }, [currentScreen, isLoadingAssets, setScreen, returnToMenu]);

  // Initialize Phaser game when screen transitions to 'game'
  useEffect(() => {
    if (currentScreen === 'game' && gameContainerRef.current && !phaserGameRef.current) {
      // Wait for container to have dimensions before initializing
      const waitForContainer = (retries = 10): Promise<void> => {
        return new Promise((resolve) => {
          if (!gameContainerRef.current) {
            resolve();
            return;
          }

          const container = gameContainerRef.current;
          const width = container.clientWidth;
          const height = container.clientHeight;

          if (width > 0 && height > 0) {
            resolve();
          } else if (retries > 0) {
            setTimeout(() => waitForContainer(retries - 1).then(resolve), 50);
          } else {
            // Use defaults if container still doesn't have dimensions
            resolve();
          }
        });
      };

      // Dynamically import modules (will use cached versions from loading screen)
      Promise.all([
        import('phaser'),
        import('@game/map/TilemapScene'),
        import('@game/map/loadMap'),
        waitForContainer(),
      ])
        .then(async ([PhaserModule, { TilemapScene }, { loadMap }]) => {
          if (!gameContainerRef.current) {
            console.error('Container ref is null after import!');
            return;
          }

          const Phaser = PhaserModule.default;
          // Load map data (from static JSON or procedurally generated)
          // Only log performance in non-test environments or when explicitly enabled
          let mapData: Awaited<ReturnType<typeof loadMap>>;
          if (process.env.NODE_ENV !== 'test' || process.env.PERF_VERBOSE === 'true') {
            const mapGenStart = performance.now();
            mapData = await loadMap();
            const mapGenTime = performance.now() - mapGenStart;
            const mapSource =
              import.meta.env.VITE_USE_STATIC_MAP === 'true' ? 'static' : 'procedural';
            console.info(
              `[Performance] Map loading (${mapSource}) took ${mapGenTime.toFixed(2)}ms`
            );
          } else {
            mapData = await loadMap();
          }

          // Create tilemap scene instance and initialize with map data
          const scene = new TilemapScene();
          scene.init(mapData);

          // Get container dimensions, fallback to defaults if not available
          const container = gameContainerRef.current;
          const containerWidth = container.clientWidth || 1200;
          const containerHeight = container.clientHeight || 900;

          phaserGameRef.current = new Phaser.Game({
            type: Phaser.AUTO,
            width: containerWidth > 0 ? containerWidth : 1200,
            height: containerHeight > 0 ? containerHeight : 900,
            parent: container,
            scene: scene,
            backgroundColor: '#1a1a1a',
          });

          // Wait for scene to be fully created before signaling ready
          // Phaser will call init() and create() automatically
          // We optimize by listening for the 'create' event directly
          let sceneReadyHandled = false;

          const handleSceneReady = () => {
            if (sceneReadyHandled) return; // Prevent multiple calls
            sceneReadyHandled = true;

            // Helper to complete the scene ready process
            const completeSceneReady = (gameScene: unknown) => {
              setIsGameReady(true);

              // Initial resize after game is ready
              if (gameContainerRef.current && phaserGameRef.current) {
                const container = gameContainerRef.current;
                const game = phaserGameRef.current;
                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;

                if (containerWidth > 0 && containerHeight > 0 && game.scale) {
                  try {
                    game.scale.resize(containerWidth, containerHeight);
                    const canvas = container.querySelector('canvas');
                    if (canvas) {
                      canvas.style.width = '100%';
                      canvas.style.height = '100%';
                    }
                  } catch (error) {
                    console.warn('Initial game resize error:', error);
                  }
                }
              }

              // Use ref to get latest options without causing re-renders
              const currentOptions = optionsRef.current;
              if (currentOptions?.onGameReady) {
                currentOptions.onGameReady(phaserGameRef.current);
              }
              if (currentOptions?.onSceneReady) {
                currentOptions.onSceneReady(gameScene);
              }
            };

            // Use requestAnimationFrame to ensure rendering has started
            const rafCallback = () => {
              // Poll until scene is fully initialized (create() has completed)
              let pollCount = 0;
              const maxPolls = 100; // Max 5 seconds (100 * 50ms)

              const waitForFullInit = () => {
                const gameScene = phaserGameRef.current?.scene?.getScene('TilemapScene') || scene;
                const sceneWithInit = gameScene as { isFullyInitialized?: () => boolean };

                // Check if the method exists - if not, assume ready (for tests/mocks)
                const hasInitMethod = typeof sceneWithInit?.isFullyInitialized === 'function';
                const isFullyInit = hasInitMethod
                  ? (sceneWithInit?.isFullyInitialized?.() ?? true)
                  : true;

                if (isFullyInit || pollCount >= maxPolls) {
                  completeSceneReady(gameScene);
                } else {
                  // Scene not fully initialized yet, poll again in 50ms
                  pollCount++;
                  setTimeout(waitForFullInit, 50);
                }
              };

              waitForFullInit();
            };

            if (typeof requestAnimationFrame !== 'undefined') {
              requestAnimationFrame(rafCallback);
            } else {
              // Fallback for environments without requestAnimationFrame
              setTimeout(rafCallback, 0);
            }
          };

          // Optimized scene readiness detection
          // Approach 1: Listen for create event on the scene instance
          if (scene && typeof scene === 'object') {
            const sceneAny = scene as unknown as {
              sys?: { isActive?: () => boolean; settings?: { status?: number } };
              events?: { once?: (event: string, callback: () => void) => void };
            };

            // Check if scene is already active/running
            const sceneSys = sceneAny.sys;
            const isActive = sceneSys?.isActive ? sceneSys.isActive() : false;
            const status = sceneSys?.settings?.status;

            if (isActive || status === 2) {
              // Scene is already active/running
              handleSceneReady();
            } else if (sceneAny.events?.once) {
              // Listen for create event (most reliable method)
              sceneAny.events.once('create', () => {
                handleSceneReady();
              });
            }
          }

          // Approach 2: Get scene from game and check status (faster retries)
          let retryCount = 0;
          const maxRetries = 20; // Reduced from 100 to 20 (20 * 5ms = 100ms max wait)

          const checkSceneReady = () => {
            if (sceneReadyHandled) return; // Prevent multiple calls

            const gameScene = phaserGameRef.current?.scene?.getScene('TilemapScene');
            if (gameScene) {
              const gameSceneAny = gameScene as unknown as {
                sys?: { isActive?: () => boolean; settings?: { status?: number } };
                events?: { once?: (event: string, callback: () => void) => void };
              };

              // Check if scene is already running/active
              const sceneSys = gameSceneAny.sys;
              const isActive = sceneSys?.isActive ? sceneSys.isActive() : false;
              const status = sceneSys?.settings?.status;

              if (isActive || status === 2) {
                handleSceneReady();
                return;
              }

              // Listen for create event if events are available
              if (gameSceneAny.events?.once) {
                gameSceneAny.events.once('create', () => {
                  handleSceneReady();
                });
              }
            } else if (retryCount < maxRetries) {
              // Scene not available yet, retry after a shorter delay
              retryCount++;
              setTimeout(checkSceneReady, 5); // Reduced from 10ms to 5ms
            } else {
              // Max retries reached, assume ready (fallback for edge cases)
              handleSceneReady();
            }
          };

          // Start checking for scene readiness immediately (no initial delay)
          checkSceneReady();

          // Fallback: If nothing triggers ready within 500ms, force it (reduced from 2000ms)
          setTimeout(() => {
            if (!sceneReadyHandled) {
              handleSceneReady();
            }
          }, 500); // Reduced from 2000ms to 500ms
        })
        .catch((error) => {
          console.error('Failed to initialize game:', error);
          returnToMenu();
        });
    }
  }, [currentScreen, returnToMenu, gameContainerRef]);

  // Handle game container resize to fill available space
  useEffect(() => {
    if (currentScreen !== 'game' || !isGameReady) {
      return;
    }

    if (!gameContainerRef.current || !phaserGameRef.current) {
      return;
    }

    let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const resizeGame = () => {
      if (!gameContainerRef.current || !phaserGameRef.current) return;

      const container = gameContainerRef.current;
      const game = phaserGameRef.current;

      // Get container dimensions
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Only resize if container has valid dimensions
      if (containerWidth > 0 && containerHeight > 0) {
        try {
          // Resize using Phaser's scale manager
          if (game.scale) {
            game.scale.resize(containerWidth, containerHeight);
          }

          // Also ensure canvas fills container via CSS
          const canvas = container.querySelector('canvas');
          if (canvas) {
            canvas.style.width = '100%';
            canvas.style.height = '100%';
          }
        } catch (error) {
          // Silently handle resize errors (game might not be fully initialized)
          console.warn('Game resize error:', error);
        }
      }
    };

    // Debounced resize function
    const debouncedResize = () => {
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      resizeTimeoutId = setTimeout(resizeGame, 50);
    };

    // Initial resize with a delay to ensure container is laid out and game is ready
    const initialTimeoutId = setTimeout(() => {
      resizeGame();
    }, 200);

    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      debouncedResize();
    });

    resizeObserver.observe(gameContainerRef.current);

    // Also listen to window resize as fallback
    window.addEventListener('resize', debouncedResize);

    return () => {
      clearTimeout(initialTimeoutId);
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', debouncedResize);
    };
  }, [currentScreen, isGameReady]);

  // Cleanup Phaser game when returning to menu
  useEffect(() => {
    if (currentScreen === 'menu' && phaserGameRef.current) {
      phaserGameRef.current.destroy?.(true);
      phaserGameRef.current = null;
      setIsGameReady(false);
    }
  }, [currentScreen]);

  return {
    phaserGameRef,
    isGameReady,
    isLoadingAssets,
  };
}
